'use client';

import { Card, Link, Skeleton, Stack, StackItem, Text } from '@pie/amber';
import { formatShortIsoDate } from '@upms/dates';
import NextLink from 'next/link';
import { PolicyLifecycleStatusTag } from '@/components/PolicyLifecycleStatusTag';
import { IssuedSearchPolicy } from '@/hooks/useSearchPolicies';
import { PolicyActionsMenu } from './PolicyActionsMenu';
import { PolicyPaymentInfo, PolicyPaymentInfoLabelValueSkeleton } from './PolicyPaymentInfo';

type Props = {
  policy: IssuedSearchPolicy;
};

export const PolicyCardSkeleton = () => {
  return (
    <Card data-testid="your-policies-loading" padding="layout-sm">
      <Stack gap="layout-md">
        <Stack gap="layout-xs">
          <Skeleton className="w-2/5" size="header-md" />
          <Stack direction="row" gap="sm">
            <Skeleton className="w-1/4" size="body-xs" />
            <Skeleton className="w-1/4" size="body-xs" />
          </Stack>
        </Stack>
        <PolicyPaymentInfoLabelValueSkeleton />
      </Stack>
    </Card>
  );
};

export const PolicyCard = ({ policy }: Props) => {
  const term = `${formatShortIsoDate(policy.effectiveDate)} - ${formatShortIsoDate(policy.expirationDate)}`;

  return (
    <Card padding="layout-sm">
      <Stack gap="layout-md">
        <Stack align="start" direction="row" gap="layout-md" justify="between">
          <Stack gap="layout-xs">
            <Stack
              align={{ md: 'center' }}
              // TODO: not ideal because it is not accessible and not supported by Amber
              className="md:flex-row-reverse"
              gap="layout-2xs"
              justify={{ md: 'end' }}
            >
              <StackItem self={{ md: 'center' }}>
                <PolicyLifecycleStatusTag policy={policy} size="sm" />
              </StackItem>
              <StackItem>
                <Link
                  as={NextLink}
                  href={`/policy/${policy.policyId}`}
                  truncate
                  variant="header-md"
                >
                  {policy.lastSegment.primaryLegalEntity.entityName}
                </Link>
              </StackItem>
            </Stack>
            {/* TODO(EE-305): replace className with Text tone="subtle" once Amber ships the prop */}
            <Stack direction={{ base: 'column', sm: 'row' }} gap={{ base: 'none', sm: 'md' }}>
              {policy.policyNumber !== null && (
                <Text className="text-text-subtle-default" variant="body-xs">
                  {policy.policyNumber}
                </Text>
              )}
              <Text className="text-text-subtle-default" variant="body-xs">
                {term}
              </Text>
            </Stack>
          </Stack>
          <PolicyActionsMenu policy={policy} />
        </Stack>
        <PolicyPaymentInfo policyId={policy.policyId} />
      </Stack>
    </Card>
  );
};
